import { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useOktaAuth } from '@okta/okta-react';

import { AuthProvider } from '@/providers/AuthProvider.tsx';
import { useGetAllFlagsQuery } from '@/store/slices/api/featureFlagsApiSlice';

import { MainAppShell } from '@/containers/AppShell';
import LoadingPage from '@/pages/common/LoadingPage';

const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: featureFlags, isLoading } = useGetAllFlagsQuery();

  if (isLoading || !featureFlags) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};

const AuthGuard = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Handle redirect - only trigger once per mount lifecycle
  useEffect(() => {
    const initiateRedirect = async () => {
      if (!authState) {
        return;
      }

      if (!authState.isAuthenticated && !hasRedirected.current) {
        const transaction = await oktaAuth.tx.exists();

        if (!transaction) {
          hasRedirected.current = true;
          oktaAuth.setOriginalUri(location.pathname + location.search);
          await oktaAuth.signInWithRedirect();
        }
      }
    };

    initiateRedirect();
  }, [authState, oktaAuth, location.pathname, location.search]);

  if (!authState) {
    return (
      <LoadingPage title="Authenticating" message="Please wait while we verify your identity..." />
    );
  }

  if (authState?.isAuthenticated) {
    return (
      <AuthProvider>
        <FeatureFlagsProvider>
          <MainAppShell>
            <Suspense
              fallback={
                <LoadingPage
                  title="Getting Things Ready"
                  message="Setting things up for you, hang tight..."
                />
              }>
              <Outlet />
            </Suspense>
          </MainAppShell>
        </FeatureFlagsProvider>
      </AuthProvider>
    );
  }

  // Show loading while redirect is in progress (handled by useEffect above)
  return (
    <LoadingPage
      title="Redirecting"
      message={"It seems you're not authenticated. Redirecting you to login..."}
    />
  );
};

export default AuthGuard;
